import { useState, useEffect } from "react";
import Logo from "../../assets/logo-dominus-vetorizada.png";
import {
  Instagram,
  Facebook,
  Mail,
  ExternalLink,
  ShoppingCart,
} from "lucide-react";
import MapEmbed from "../../components/MapEmbed/MapEmbed";

export default function LinkBio() {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", checkMobile);
    checkMobile(); // Verificar no carregamento inicial

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Featured products
  const featuredProducts = [
    {
      id: 1,
      name: "Smartphone Ultra X",
      description: "O mais avançado smartphone com IA integrada",
      price: "R$ 2.999,00",
      originalPrice: "R$ 3.499,00",
      image: "/placeholder.svg?height=200&width=200",
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === featuredProducts.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? featuredProducts.length - 1 : prev - 1
    );
  };

  // Render product card
  const renderProductCard = (product) => (
    <div className="w-full px-2">
      <div className="relative block overflow-hidden rounded-lg transition-transform duration-300 hover:scale-[1.02]">
        <div className="absolute -inset-1 animate-pulse rounded-lg bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 opacity-30 blur-sm transition duration-1000 group-hover:opacity-100 group-hover:duration-200"></div>
        <div className="relative flex h-full flex-col rounded-lg border border-green-500/30 bg-black/60 p-4 backdrop-blur-sm">
          <div className="mb-3 flex items-center justify-center">
            <img
              src={product.image}
              alt={product.name}
              width={150}
              height={150}
              className="h-[150px] w-[150px] object-contain"
            />
          </div>
          <h3 className="mb-1 text-lg font-medium text-white">
            {product.name}
          </h3>
          <p className="mb-2 text-xs text-gray-400 line-clamp-2">
            {product.description}
          </p>
          <div className="mt-auto flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-green-400">
                {product.price}
              </p>
              <p className="text-xs text-gray-500 line-through">
                {product.originalPrice}
              </p>
            </div>
            <button className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-green-600 hover:to-emerald-700 focus:outline-none">
              <ShoppingCart className="mr-1 h-4 w-4" />
              Comprar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black p-4">
      {/* Main content */}
      <div className="relative z-10 w-full max-w-3xl space-y-8 py-8">
        {/* Profile section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="group relative">
            <div className="absolute -inset-0.5 animate-pulse rounded-full bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 opacity-75 blur-sm transition duration-1000 group-hover:opacity-100 group-hover:duration-200"></div>
            <div className="relative h-28 w-28 overflow-hidden rounded-full border-2 border-green-500/50 bg-black">
              <img
                src={Logo}
                alt="Store logo"
                width={112}
                height={112}
                className="object-cover"
              />
            </div>
          </div>

          <div className="text-center">
            <h1 className="bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 bg-clip-text text-3xl font-bold text-transparent">
              Dominus
            </h1>
            <p className="text-green-400">@dominustech_</p>
            <p className="mt-3 text-sm text-gray-400">Seja Bem-vindo</p>
          </div>
        </div>

        {/* Featured promotions */}
        <div className="space-y-4">
          <h2 className="text-center text-xl font-bold text-white">
            <span className="bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
              PROMOÇÕES EM DESTAQUE
            </span>
          </h2>

          {isMobile ? (
            <div className="relative overflow-hidden w-full">
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{
                  width: `${featuredProducts.length * 100}%`,
                  transform: `translateX(-${
                    (currentSlide * 100) / featuredProducts.length
                  }%)`,
                }}
              >
                {featuredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="w-full flex-shrink-0 px-2"
                    style={{
                      width: `${100 / featuredProducts.length}%`,
                    }}
                  >
                    {renderProductCard(product)}
                  </div>
                ))}
              </div>

              {/* Navigation buttons */}
              <button
                onClick={prevSlide}
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Slide indicators */}
              <div className="mt-4 flex justify-center space-x-2">
                {featuredProducts.map((_, index) => (
                  <button
                    key={`indicator-${index}`}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                      index === currentSlide ? "bg-green-400" : "bg-gray-600"
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : (
            /* Grid for desktop */
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {featuredProducts.map((product) => (
                <div key={product.id} className="group">
                  {renderProductCard(product)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick links */}
        <div className="space-y-4">
          <h2 className="text-center text-xl font-bold text-white">
            <span className="bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
              LINKS RÁPIDOS
            </span>
          </h2>

          {[
            { text: "Nossa loja online", icon: ShoppingCart, url: "/" },
            {
              text: "Produtos em Promoção",
              icon: ExternalLink,
              url: "/produtos",
            },
            {
              text: "Nosso WhatsApp",
              icon: ExternalLink,
              url: "https://wa.me/556184497981",
            },
          ].map((link, index) => (
            <a
              key={`link-${index}`}
              href={link.url}
              target={link.url.startsWith("http") ? "_blank" : undefined}
              rel={link.url.startsWith("http") ? "noreferrer" : undefined}
              className="group relative block overflow-hidden rounded-lg"
            >
              <div className="absolute -inset-1 animate-pulse rounded-lg bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 opacity-30 blur-sm transition duration-1000 group-hover:opacity-100 group-hover:duration-200"></div>
              <div className="relative flex items-center justify-between rounded-lg border border-green-500/30 bg-black/60 p-4 backdrop-blur-sm transition-all duration-300 group-hover:bg-black/80">
                <span className="text-lg font-medium text-white">
                  {link.text}
                </span>
                <link.icon className="h-5 w-5 text-green-400 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </a>
          ))}
        </div>

        {/* Map section */}
        <div className="space-y-4">
          <h2 className="text-center text-xl font-bold text-white">
            <span className="bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
              LOCALIZAÇÃO
            </span>
          </h2>
          <MapEmbed />
        </div>

        {/* Social media */}
        <div className="flex justify-center space-x-4 pt-6">
          {[
            {
              icon: Instagram,
              label: "Instagram",
              url: "https://www.instagram.com/dominustech_/",
            },
            {
              icon: Facebook,
              label: "Facebook",
              url: "https://www.facebook.com/dominustech",
            },
            {
              icon: Mail,
              label: "Email",
              url: "mailto:dominusassistencia@gmail.com",
            },
          ].map((social) => (
            <a
              key={social.label}
              href={social.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-black/60 p-3 text-green-400 transition-colors hover:bg-black/80 hover:text-green-300"
              aria-label={social.label}
            >
              <social.icon className="h-6 w-6" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
